import { useQuery, useQueryClient } from "@tanstack/solid-query";
import {
  SOCIAL_PLATFORM,
  SOCIAL_PLATFORMS,
  type SocialPlatform,
  SocialPlatformSchema,
} from "contracts/enums/social";
import { SOCIAL_PLATFORM_LABELS } from "contracts/enums/social-labels";
import { createSignal, For, Show } from "solid-js";
import { api } from "../../store/api";
import { errorMessage } from "../../store/api-client";
import { socialEnabled } from "../../store/demo";
import { SocialProfileCard } from "./SocialProfileCard";

export function SocialSettings(props: { clientId: string }) {
  const queryClient = useQueryClient();
  const [platform, setPlatform] = createSignal<SocialPlatform>(
    SOCIAL_PLATFORM.INSTAGRAM,
  );
  const [profile, setProfile] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");

  const workspace = useQuery(() => ({
    queryKey: ["social-workspace", props.clientId],
    queryFn: () => api.social.readSocialWorkspace(props.clientId),
    enabled: socialEnabled(),
  }));

  const refresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["social-workspace", props.clientId],
    });
  };
  const mutate = async (operation: () => Promise<object>) => {
    setBusy(true);
    setError("");
    try {
      await operation();
      await refresh();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };
  const register = async (event: SubmitEvent) => {
    event.preventDefault();
    await mutate(() =>
      api.social.registerSocialProfile({
        clientId: props.clientId,
        platform: platform(),
        profile: profile(),
      }),
    );
    if (!error()) setProfile("");
  };

  return (
    <section class="editor social-admin">
      <p>
        Public profiles and extraction controls apply to this Client. Read
        evidence and findings in Social.
      </p>
      <div class="social-card-head">
        <h2>Social profiles</h2>
        <button
          class="btn"
          type="button"
          disabled={!props.clientId || workspace.isFetching}
          onClick={refresh}
        >
          Refresh
        </button>
      </div>
      <form class="social-register" onSubmit={register}>
        <label class="editor-field">
          <span class="editor-field-label">Platform</span>
          <select
            class="editor-input"
            value={platform()}
            onChange={(event) =>
              setPlatform(SocialPlatformSchema.parse(event.currentTarget.value))
            }
          >
            <For each={SOCIAL_PLATFORMS}>
              {(value) => (
                <option value={value}>{SOCIAL_PLATFORM_LABELS[value]}</option>
              )}
            </For>
          </select>
        </label>
        <label class="editor-field social-profile-input">
          <span class="editor-field-label">Public profile URL or handle</span>
          <input
            class="editor-input"
            required
            value={profile()}
            placeholder="@profile or https://…"
            onInput={(event) => setProfile(event.currentTarget.value)}
          />
        </label>
        <button
          class="btn btn-primary"
          type="submit"
          disabled={busy() || !profile().trim()}
        >
          Register
        </button>
      </form>

      <Show when={error()}>
        {(message) => <div class="gate-error">{message()}</div>}
      </Show>
      <Show
        when={workspace.data}
        fallback={
          <Show when={props.clientId}>
            <div class="loading">Loading social content…</div>
          </Show>
        }
      >
        {(data) => (
          <>
            <div class="social-profile-list">
              <For each={data().profiles}>
                {(item) => (
                  <SocialProfileCard
                    profile={item}
                    extraction={data().extractions.find(
                      (run) => run.profileId === item.id,
                    )}
                    busy={busy()}
                    onStart={() =>
                      void mutate(() =>
                        api.social.startSocialExtraction(item.id),
                      )
                    }
                    onStop={(runId) =>
                      void mutate(() => api.social.stopSocialExtraction(runId))
                    }
                    onResume={(runId) =>
                      void mutate(() =>
                        api.social.resumeSocialExtraction(runId),
                      )
                    }
                  />
                )}
              </For>
            </div>
          </>
        )}
      </Show>
    </section>
  );
}
